<script setup>
import { computed, nextTick, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Dialog from "primevue/dialog";
import FloatLabel from "primevue/floatlabel";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Select from "primevue/select";
import DatePicker from "primevue/datepicker";
import { Icon } from "@iconify/vue";
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { useBreakpoint } from "@/composables/useBreakpoint";
import { useEditableTimePicker } from "@/composables/useEditableTimePicker.js";
import { useConfigData } from "@/composables/useConfigData.js";
import { useConfigStore } from "@/stores/config.js";
import { useCustomerReview } from "@/composables/model/customerReview/useCustomerReview.js";
import { useUserStore } from "@/stores/user.js";
import { PERMISSIONS } from "@/constant/permissions.js";
import Customers from "@/components/Customers.vue";
import Agents from "@/components/Agents.vue";
import {
  REVIEW_COMPLAINT_REASON_OPTIONS,
  REVIEW_LOSS_REASON_OPTIONS,
  REVIEW_OUTCOME_LOST,
  REVIEW_OUTCOME_OPTIONS,
  REVIEW_REQUEST_OPTIONS,
  REVIEW_SECTION_OPTIONS,
  REVIEW_TYPE_OPTIONS,
  configServiceForSection,
} from "@/constant/customerReview.js";
import {
  clientSelectionFromReview,
  gamesForReviewSection,
  isGameFieldDisabled,
  withCurrentGameOption,
} from "@/utils/customerReview.js";

const props = defineProps({
  visible: { type: Boolean, default: false },
  review: { type: Object, default: null },
});

const emit = defineEmits(["update:visible", "saved", "delete"]);

const { t } = useI18n();
const { isXL } = useBreakpoint();
const configStore = useConfigStore();
const { createReview, updateReview, saving } = useCustomerReview();
const userStore = useUserStore();
const canDelete = computed(() => userStore.hasPermission(PERMISSIONS.BIG_BOSS));

const isEdit = computed(
  () =>
    (props.review?.sessionID ?? props.review?.id) !== null &&
    (props.review?.sessionID ?? props.review?.id) !== undefined,
);
const client = ref(null);
const agentProfile = ref(null);

const form = reactive(emptyForm());

const rules = computed(() => ({
  ...(isEdit.value ? { sessionID: { required } } : {}),
  clientID: { required },
  type: { required },
  section: { required },
  openedAt: { required },
  requestKind: { required },
  outcome: { required },
}));

const $v = useVuelidate(rules, form);

const sectionService = computed(() => configServiceForSection(form.section));
const { games: serviceGames } = useConfigData(sectionService);
const allGames = computed(() => configStore.getAllGames || []);
const gameDisabled = computed(() =>
  isGameFieldDisabled(form.section, isEdit.value),
);
const gameOptions = computed(() => {
  if (gameDisabled.value) return [];
  const options = gamesForReviewSection(
    form.section,
    allGames.value,
    serviceGames.value,
  );
  return withCurrentGameOption(options, form.game);
});

const openedAtModel = computed({
  get() {
    return timestampToDate(form.openedAt);
  },
  set(value) {
    form.openedAt = value instanceof Date ? value.getTime() : null;
  },
});

const firstReplyModel = computed({
  get() {
    return timestampToDate(form.firstReply);
  },
  set(value) {
    form.firstReply = value instanceof Date ? value.getTime() : null;
  },
});

const { timePt: openedAtTimePt } = useEditableTimePicker(openedAtModel);
const { timePt: firstReplyTimePt } = useEditableTimePicker(firstReplyModel);

const dialogVisible = computed({
  get() {
    return props.visible;
  },
  set(value) {
    emit("update:visible", value);
  },
});

const hydrating = ref(false);
const customerKey = ref(0);

watch([() => props.visible, () => props.review], () => {
  if (!props.visible) return;
  fillForm(props.review);
});

watch(client, (value) => {
  form.clientID = value?.id ?? null;
});

watch(
  () => form.outcome,
  (outcome) => {
    if (hydrating.value) return;
    if (outcome !== REVIEW_OUTCOME_LOST) form.lossReason = null;
  },
);

watch(
  () => form.section,
  () => {
    if (hydrating.value) return;
    if (gameDisabled.value) {
      form.game = null;
      return;
    }
    const allowedGames = gamesForReviewSection(
      form.section,
      allGames.value,
      serviceGames.value,
    );
    if (form.game && !allowedGames.some((game) => game.game === form.game)) {
      form.game = null;
    }
  },
);

function emptyForm() {
  return {
    id: null,
    sessionID: null,
    clientID: null,
    type: null,
    section: null,
    openedAt: null,
    game: null,
    agentID: null,
    firstReply: null,
    requestKind: null,
    outcome: null,
    lossReason: null,
    complaintReason: null,
    note: "",
  };
}

function fillForm(review) {
  hydrating.value = true;
  const next = emptyForm();
  if (review) {
    next.sessionID = review.sessionID ?? review.id ?? null;
    next.id = next.sessionID;
    next.clientID = review.clientID ?? review.client?.id ?? null;
    next.type = review.type ?? null;
    next.section = review.section ?? null;
    next.openedAt = review.openedAt ?? null;
    next.game = review.game ?? null;
    next.agentID = review.agentID ?? null;
    next.firstReply = review.firstReply ?? null;
    next.requestKind = review.requestKind ?? null;
    next.outcome = review.outcome ?? null;
    next.lossReason = review.lossReason ?? null;
    next.complaintReason = review.complaintReason ?? null;
    next.note = review.note ?? "";
  }
  Object.assign(form, next);
  client.value = review ? clientSelectionFromReview(review) : null;
  agentProfile.value = review?.agent ?? null;
  customerKey.value += 1;
  $v.value.$reset();
  nextTick(() => {
    hydrating.value = false;
  });
}

function timestampToDate(value) {
  if (value && !Number.isNaN(Number(value))) return new Date(Number(value));
  return null;
}

function selectOpenedAtNow() {
  form.openedAt = Date.now();
}

function clearOpenedAt() {
  form.openedAt = null;
}

function selectFirstReplyNow() {
  form.firstReply = Date.now();
}

function clearFirstReply() {
  form.firstReply = null;
}

function optionLabel(option) {
  return t(option.labelKey);
}

function fieldInvalid(name) {
  return Boolean($v.value[name]?.$error);
}

function closeModal() {
  dialogVisible.value = false;
}

async function submit() {
  $v.value.$touch();
  if ($v.value.$invalid || saving.value) return;
  const result = isEdit.value
    ? await updateReview(form)
    : await createReview(form);
  if (result?.success) {
    emit("saved");
    closeModal();
  }
}

function requestDelete() {
  if (!props.review || saving.value) return;
  emit("delete", props.review);
}
</script>

<template>
  <Dialog
    v-model:visible="dialogVisible"
    :class="[
      'min-w-max min-h-max !border-0 !rounded-md !bg-[#0e1726]',
      isXL ? '' : 'w-full',
    ]"
    :position="isXL ? 'center' : 'bottom'"
    closable
    dismissable-mask
    draggable
    modal
    content-class="min-w-full flex flex-col gap-4 overflow-hidden w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]"
  >
    <template #header>
      <div class="text-base py-3">
        {{
          isEdit
            ? t("customerSession.editTitle")
            : t("customerSession.createTitle")
        }}
      </div>
    </template>
    <template #closebutton="{ closeCallback }">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
        type="button"
        class="text-white-dark outline-none hover:text-red-500"
        @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs" />
      </button>
    </template>

    <form
      class="flex flex-col gap-4 overflow-y-auto pt-2 pb-1 text-xs 2xl:text-sm md:min-w-[640px]"
      @submit.prevent="submit"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-if="isEdit" class="flex flex-col gap-0.5">
          <FloatLabel variant="on">
            <InputText
              id="review-session-id"
              :modelValue="form.sessionID"
              disabled
              class="!bg-slate-800 w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
            />
            <label for="review-session-id" class="!bg-slate-900">{{
              t("customerSession.fields.sessionId")
            }}</label>
          </FloatLabel>
        </div>

        <Customers
          v-model="client"
          :noAutoCompleteWithUsers="true"
          overlay-class="!bg-[#0F172A] !rounded-md"
          :label="t('customerSession.fields.client')"
          :required="true"
          append-to="body"
        />

        <div class="flex flex-col gap-0.5">
          <FloatLabel
            class="w-full custom-select"
            :class="{ 'has-error': fieldInvalid('type') }"
            variant="on"
          >
            <Select
              v-model="form.type"
              inputId="review-type"
              :options="REVIEW_TYPE_OPTIONS"
              :optionLabel="optionLabel"
              optionValue="value"
              class="w-full !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
              :class="
                fieldInvalid('type')
                  ? '!border !border-red-300'
                  : '!border-slate-700'
              "
              overlayClass="custom-select-overlay"
            />
            <label for="review-type" class="!bg-slate-900">{{
              t("customerSession.fields.type")
            }}</label>
          </FloatLabel>
          <p v-if="fieldInvalid('type')" class="text-red-300 mt-1 text-xs">
            {{ t("validation.required") }}
          </p>
        </div>

        <div class="flex flex-col gap-0.5">
          <FloatLabel
            class="w-full custom-select"
            :class="{ 'has-error': fieldInvalid('section') }"
            variant="on"
          >
            <Select
              v-model="form.section"
              inputId="review-section"
              :options="REVIEW_SECTION_OPTIONS"
              :optionLabel="optionLabel"
              optionValue="value"
              class="w-full !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
              :class="
                fieldInvalid('section')
                  ? '!border !border-red-300'
                  : '!border-slate-700'
              "
              overlayClass="custom-select-overlay"
            />
            <label for="review-section" class="!bg-slate-900">{{
              t("customerSession.fields.section")
            }}</label>
          </FloatLabel>
          <p v-if="fieldInvalid('section')" class="text-red-300 mt-1 text-xs">
            {{ t("validation.required") }}
          </p>
        </div>

        <div class="flex flex-col gap-0.5">
          <FloatLabel
            variant="on"
            :class="{ 'has-error': fieldInvalid('openedAt') }"
          >
            <DatePicker
              inputId="review-opened-at"
              v-model="openedAtModel"
              :pt="openedAtTimePt"
              showTime
              hourFormat="24"
              dateFormat="d M yy"
              fluid
              :showButtonBar="true"
              size="small"
              input-class="!border-slate-700 !bg-transparent h-[40px]"
              showIcon
              iconDisplay="input"
            >
              <template #todaybutton>
                <span
                  class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50"
                  @click="selectOpenedAtNow"
                >
                  {{ t("customerSession.now") }}
                </span>
              </template>
              <template #clearbutton>
                <span
                  class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50"
                  @click="clearOpenedAt"
                >
                  {{ t("customerSession.clear") }}
                </span>
              </template>
            </DatePicker>
            <label for="review-opened-at" class="!bg-slate-900">{{
              t("customerSession.fields.openedAt")
            }}</label>
          </FloatLabel>
          <p v-if="fieldInvalid('openedAt')" class="text-red-300 mt-1 text-xs">
            {{ t("validation.required") }}
          </p>
        </div>

        <div class="flex flex-col gap-0.5">
          <FloatLabel class="w-full custom-select" variant="on">
            <Select
              v-model="form.game"
              inputId="review-game"
              :options="gameOptions"
              optionLabel="game"
              optionValue="game"
              :disabled="gameDisabled"
              showClear
              filter
              autoFilterFocus
              class="w-full !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
              overlayClass="custom-select-overlay"
            />
            <label for="review-game" class="!bg-slate-900">{{
              t("customerSession.fields.game")
            }}</label>
          </FloatLabel>
        </div>

        <Agents
          v-model="form.agentID"
          inputId="review-agent"
          :label="t('customerSession.fields.agent')"
          :selected-profile="agentProfile"
        />

        <div class="flex flex-col gap-0.5">
          <FloatLabel variant="on">
            <DatePicker
              inputId="review-first-reply"
              v-model="firstReplyModel"
              :pt="firstReplyTimePt"
              showTime
              hourFormat="24"
              dateFormat="d M yy"
              fluid
              :showButtonBar="true"
              size="small"
              input-class="!border-slate-700 !bg-transparent h-[40px]"
              showIcon
              iconDisplay="input"
            >
              <template #todaybutton>
                <span
                  class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50"
                  @click="selectFirstReplyNow"
                >
                  {{ t("customerSession.now") }}
                </span>
              </template>
              <template #clearbutton>
                <span
                  class="cursor-pointer px-2 py-1 rounded-md text-xs hover:bg-gray-700/50"
                  @click="clearFirstReply"
                >
                  {{ t("customerSession.clear") }}
                </span>
              </template>
            </DatePicker>
            <label for="review-first-reply" class="!bg-slate-900">{{
              t("customerSession.fields.firstReply")
            }}</label>
          </FloatLabel>
        </div>

        <div class="flex flex-col gap-0.5">
          <FloatLabel
            class="w-full custom-select"
            :class="{ 'has-error': fieldInvalid('requestKind') }"
            variant="on"
          >
            <Select
              v-model="form.requestKind"
              inputId="review-request"
              :options="REVIEW_REQUEST_OPTIONS"
              :optionLabel="optionLabel"
              optionValue="value"
              class="w-full !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
              :class="
                fieldInvalid('requestKind')
                  ? '!border !border-red-300'
                  : '!border-slate-700'
              "
              overlayClass="custom-select-overlay"
            />
            <label for="review-request" class="!bg-slate-900">{{
              t("customerSession.fields.request")
            }}</label>
          </FloatLabel>
          <p
            v-if="fieldInvalid('requestKind')"
            class="text-red-300 mt-1 text-xs"
          >
            {{ t("validation.required") }}
          </p>
        </div>

        <div class="flex flex-col gap-0.5">
          <FloatLabel
            class="w-full custom-select"
            :class="{ 'has-error': fieldInvalid('outcome') }"
            variant="on"
          >
            <Select
              v-model="form.outcome"
              inputId="review-outcome"
              :options="REVIEW_OUTCOME_OPTIONS"
              :optionLabel="optionLabel"
              optionValue="value"
              class="w-full !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
              :class="
                fieldInvalid('outcome')
                  ? '!border !border-red-300'
                  : '!border-slate-700'
              "
              overlayClass="custom-select-overlay"
            />
            <label for="review-outcome" class="!bg-slate-900">{{
              t("customerSession.fields.outcome")
            }}</label>
          </FloatLabel>
          <p v-if="fieldInvalid('outcome')" class="text-red-300 mt-1 text-xs">
            {{ t("validation.required") }}
          </p>
        </div>

        <div
          v-if="form.outcome === REVIEW_OUTCOME_LOST"
          class="flex flex-col gap-0.5"
        >
          <FloatLabel class="w-full custom-select" variant="on">
            <Select
              v-model="form.lossReason"
              inputId="review-loss-reason"
              :options="REVIEW_LOSS_REASON_OPTIONS"
              :optionLabel="optionLabel"
              optionValue="value"
              showClear
              class="w-full !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
              overlayClass="custom-select-overlay"
            />
            <label for="review-loss-reason" class="!bg-slate-900">{{
              t("customerSession.fields.lossReason")
            }}</label>
          </FloatLabel>
        </div>

        <div class="flex flex-col gap-0.5">
          <FloatLabel class="w-full custom-select" variant="on">
            <Select
              v-model="form.complaintReason"
              inputId="review-complaint-reason"
              :options="REVIEW_COMPLAINT_REASON_OPTIONS"
              :optionLabel="optionLabel"
              optionValue="value"
              showClear
              class="w-full !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
              overlayClass="custom-select-overlay"
            />
            <label for="review-complaint-reason" class="!bg-slate-900">{{
              t("customerSession.fields.complaintReason")
            }}</label>
          </FloatLabel>
        </div>

        <div class="md:col-span-2 flex flex-col gap-0.5">
          <FloatLabel variant="on">
            <Textarea
              id="review-note"
              v-model="form.note"
              rows="3"
              autoResize
              class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700"
            />
            <label for="review-note" class="!bg-slate-900">{{
              t("customerSession.fields.note")
            }}</label>
          </FloatLabel>
        </div>
      </div>

      <p v-if="fieldInvalid('clientID')" class="text-red-300 text-xs">
        {{ t("customerSession.clientRequired") }}
      </p>

      <div class="flex flex-row justify-between gap-2">
        <button
          v-if="isEdit && canDelete"
          type="button"
          class="btn btn-outline-danger py-1 text-xs"
          :disabled="saving"
          @click="requestDelete"
        >
          {{ t("message.delete") }}
        </button>
        <div class="flex flex-row gap-2 ms-auto">
          <button
            type="button"
            class="btn btn-outline-dark py-1 text-xs"
            @click="closeModal"
          >
            {{ t("message.cancel") }}
          </button>
          <button
            type="submit"
            class="btn btn-outline-primary py-1 text-xs"
            :disabled="saving"
          >
            {{ saving ? t("customerSession.saving") : t("message.submit") }}
          </button>
        </div>
      </div>
    </form>
  </Dialog>
</template>
